import chunk from 'lodash/chunk';

import type { NodeRef } from '../view/types';

export const FILE_METADATA_BATCH_SIZE = 100;

/** Retries from React Query start on separate turns. This wait lets them join one flush. */
export const FILE_METADATA_BATCH_DELAY_MS = 50;

/** A download retry repeats the whole media query. Reuse metadata that already succeeded. */
export const FILE_METADATA_CACHE_MS = 30_000;

type PendingLoad<T> = {
  ref: NodeRef;
  resolve: (file: T | undefined) => void;
  reject: (error: unknown) => void;
};

type CachedFile<T> = {
  value: T | undefined;
  storedAt: number;
};

export type ScheduleFlush = (flush: () => void) => void;

export const createScheduleFlush = (
  delayMs = FILE_METADATA_BATCH_DELAY_MS
): ScheduleFlush => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (flush) => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = undefined;
      flush();
    }, delayMs);
  };
};

const fileInstanceKey = (ref: NodeRef) => `${ref.space}:${ref.externalId}`;

const findFileForNodeRef = <T extends NodeRef>(
  files: T[],
  ref: NodeRef
): T | undefined =>
  files.find(
    (file) => file.externalId === ref.externalId && file.space === ref.space
  );

/**
 * Queues file metadata reads and flushes them after a short delay so calls
 * that start together, including React Query retries, share one files.byId.
 *
 * See docs/decisions/infield/2026-10-16-observation-file-metadata-batching.md
 */
export const createFileMetadataBatcher = <T extends NodeRef>(
  fetchByIds: (refs: NodeRef[]) => Promise<T[]>,
  scheduleFlush: ScheduleFlush = createScheduleFlush(),
  now: () => number = Date.now,
  shouldCache: (file: T | undefined) => boolean = () => true
): ((ref: NodeRef) => Promise<T | undefined>) => {
  let pending: PendingLoad<T>[] = [];
  const cache = new Map<string, CachedFile<T>>();

  const flush = async () => {
    const batch = pending;
    pending = [];

    const requestsByKey = new Map<string, PendingLoad<T>[]>();
    const uniqueRefs: NodeRef[] = [];
    batch.forEach((request) => {
      const key = fileInstanceKey(request.ref);
      const existing = requestsByKey.get(key);
      if (existing) {
        existing.push(request);
        return;
      }
      requestsByKey.set(key, [request]);
      uniqueRefs.push(request.ref);
    });

    const chunks = chunk(uniqueRefs, FILE_METADATA_BATCH_SIZE);
    await Promise.all(
      chunks.map(async (refs) => {
        const requests = refs.flatMap(
          (ref) => requestsByKey.get(fileInstanceKey(ref)) ?? []
        );

        try {
          const files = await fetchByIds(refs);
          const storedAt = now();
          requests.forEach((request) => {
            const file = findFileForNodeRef(files, request.ref);
            if (shouldCache(file)) {
              cache.set(fileInstanceKey(request.ref), {
                value: file,
                storedAt,
              });
            }
            request.resolve(file);
          });
        } catch (error) {
          requests.forEach((request) => {
            request.reject(error);
          });
        }
      })
    );
  };

  const schedule = () => {
    scheduleFlush(() => {
      void flush();
    });
  };

  return (ref: NodeRef) => {
    const cached = cache.get(fileInstanceKey(ref));
    if (cached && now() - cached.storedAt < FILE_METADATA_CACHE_MS) {
      return Promise.resolve(cached.value);
    }

    return new Promise((resolve, reject) => {
      pending.push({ ref, resolve, reject });
      schedule();
    });
  };
};
